import {
	type OrderDetailResponseDto,
	OrderTimelineKeyEnum,
} from "Infrastructure/Api/Api";
import { useOrderTimelineQuery } from "Order/Api/Query/useOrderTimelineQuery";
import { useFormatTimelineValues } from "Order/Hooks/useFormattedTimelineValue";
import {
	ORDER_PROTOCOLS_KEY,
	useOrderProtocolsQuery,
} from "Order/Modules/MorningCheck/Api/Query/useOrderProtocolsQuery";
import {
	derivePartnershipStatus,
	PARTNERSHIP_RECORDS_KEY,
	usePartnershipRecordsQuery,
} from "Order/Modules/MorningCheck/Api/Query/usePartnershipRecordsQuery";
import { MorningCheckNoteForAdamCard } from "Order/Modules/MorningCheck/Components/MorningCheckNoteForAdamCard";
import { MorningCheckStatusCard } from "Order/Modules/MorningCheck/Components/MorningCheckStatusCard";
import { MorningCheckTimeline } from "Order/Modules/MorningCheck/Components/MorningCheckTimeline";
import { buildPartnerLookupByTime } from "Order/Utilities/deriveActivePartner";
import { Loading } from "Shared/Components/Loading/Loading";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import Refresh from "@mui/icons-material/Refresh";
import {
	Alert,
	Box,
	Grid2,
	IconButton,
	Paper,
	Stack,
	Tooltip,
	Typography,
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { type FC, useCallback, useMemo } from "react";

type OrderMorningCheckTabProps = {
	order: OrderDetailResponseDto;
};

export const OrderMorningCheckTab: FC<OrderMorningCheckTabProps> = ({
	order,
}) => {
	const queryClient = useQueryClient();
	const {
		data: records,
		isLoading,
		isError,
		error,
	} = usePartnershipRecordsQuery(order.id);

	const { data: protocols } = useOrderProtocolsQuery(order.id);

	// Fetch order timeline to derive which partner was active at each morning check step
	const { data: timelineEntries } = useOrderTimelineQuery(order.id);

	const getPartnerAtTime = useMemo(
		() =>
			timelineEntries ? buildPartnerLookupByTime(timelineEntries) : undefined,
		[timelineEntries],
	);

	// Resolve partner ID → display name via the same hook used by the order timeline.
	// The hook uses useMarket() internally for partner list, so the market param
	// is only needed for currency formatting — safe to pass a fallback.
	const formatValue = useFormatTimelineValues(
		order.market ??
			({
				country: "",
				currency: "",
			} as unknown as import("Infrastructure/Api/Api").MarketResponseDto),
	);

	const resolvePartnerName = useCallback(
		(partnerId: string) => {
			const name = formatValue(OrderTimelineKeyEnum.partnerId, partnerId);
			return typeof name === "string" ? name : partnerId;
		},
		[formatValue],
	);

	const handleRefresh = () => {
		queryClient.invalidateQueries({
			queryKey: [PARTNERSHIP_RECORDS_KEY, order.id],
		});
		queryClient.invalidateQueries({
			queryKey: [ORDER_PROTOCOLS_KEY, order.id],
		});
	};

	if (isLoading) {
		return (
			<Stack
				direction="column"
				justifyContent="center"
				alignItems="center"
				spacing={2}
				sx={{ height: "100%", py: 4 }}
			>
				<Loading />
				<Typography variant="body2" color="text.secondary">
					Loading partner updates...
				</Typography>
			</Stack>
		);
	}

	if (isError) {
		return (
			<Alert severity="error" sx={{ mt: 1 }}>
				Failed to load partner updates
				{error instanceof Error ? `: ${error.message}` : ""}
			</Alert>
		);
	}

	const partnershipRecords = records ?? [];
	const protocolRecords = protocols ?? [];
	const status = derivePartnershipStatus(partnershipRecords, protocolRecords);

	return (
		<Stack gap={2}>
			{/* Header */}
			<Stack direction="row" justifyContent="space-between" alignItems="center">
				<Stack direction="row" alignItems="center" gap={1}>
					<Typography variant="h6" fontWeight={600}>
						Morning Check
					</Typography>
					<Tooltip title="Partner updates from the partner app. Auto-refreshes every 30 seconds.">
						<InfoOutlined fontSize="small" color="action" />
					</Tooltip>
				</Stack>
				<Tooltip title="Refresh now">
					<IconButton onClick={handleRefresh} size="small">
						<Refresh fontSize="small" />
					</IconButton>
				</Tooltip>
			</Stack>

			{partnershipRecords.length === 0 && protocolRecords.length === 0 ? (
				<Paper
					variant="outlined"
					sx={{
						p: 4,
						textAlign: "center",
					}}
				>
					<Typography variant="body1" color="text.secondary" gutterBottom>
						No partner updates yet
					</Typography>
					<Typography variant="body2" color="text.secondary">
						Partner updates will appear here once the partner submits their
						morning check through the partner app.
					</Typography>
				</Paper>
			) : (
				<Grid2 container spacing={2} alignItems="flex-start">
					{/* Timeline — main column */}
					<Grid2 size={{ xs: 12, md: 8 }}>
						<Paper variant="outlined" sx={{ p: 2 }}>
							<Typography
								variant="subtitle2"
								fontWeight={600}
								gutterBottom
								color="text.secondary"
							>
								Activity Timeline
							</Typography>
							<Box mt={1}>
								<MorningCheckTimeline
									records={partnershipRecords}
									protocols={protocolRecords}
									getPartnerAtTime={getPartnerAtTime}
									resolvePartnerName={resolvePartnerName}
								/>
							</Box>
						</Paper>
					</Grid2>

					{/* Status sidebar */}
					<Grid2 size={{ xs: 12, md: 4 }}>
						<Stack gap={2}>
							<MorningCheckStatusCard status={status} />
							<MorningCheckNoteForAdamCard records={partnershipRecords} />
						</Stack>
					</Grid2>
				</Grid2>
			)}
		</Stack>
	);
};
